import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import { User } from "lucide-react";
import { api } from "@/lib/api";
import { SiteNav } from "@/components/public/SiteNav";
import { SiteFooter } from "@/components/public/SiteFooter";
import { Reveal } from "@/components/public/Reveal";
import { IMAGES } from "@/lib/images";

export default function About() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const stats = settings?.impact_stats || [];
  const copy = settings?.about_copy || "";

  return (
    <div className="bg-cream">
      <SiteNav />

      {/* Hero */}
      <section className="max-w-content mx-auto px-6 md:px-12 pt-32 md:pt-44 pb-10" data-testid="about-hero">
        <Reveal as="span" className="block text-xs uppercase tracking-[0.25em] font-bold text-sylhet-green">The Patron</Reveal>
        <Reveal as="h1" className="font-display text-5xl md:text-8xl font-bold tracking-tight mt-4 leading-[0.95] max-w-4xl text-balance">
          Fahim Al Choudhury
        </Reveal>
        <Reveal as="p" delay={0.1} className="text-charcoal/55 mt-5 text-lg">Entrepreneur · Philanthropist · Patron of Made in Sylhet</Reveal>
      </section>

      {/* Editorial split */}
      <section className="max-w-content mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5 md:sticky md:top-28">
            <Reveal className="rounded-2xl overflow-hidden bg-sylhet-forest aspect-[4/5] grid place-items-center relative grain" data-testid="portrait-frame">
              <div className="text-center text-cream/50 relative z-10 px-6">
                <div className="w-20 h-20 mx-auto rounded-full border border-cream/25 grid place-items-center mb-4">
                  <User size={36} />
                </div>
                <p className="font-display text-lg text-cream/70">Official portrait</p>
                <p className="text-sm mt-1 text-cream/40">to be supplied by the patron's office</p>
              </div>
            </Reveal>
            <p className="text-charcoal/40 text-xs mt-3 italic">Placeholder frame — real photograph pending client approval.</p>
          </div>

          <div className="md:col-span-7">
            <Reveal as="p" className="font-display text-2xl md:text-3xl font-medium leading-snug text-charcoal">
              {copy}
            </Reveal>

            <div className="mt-10 grid sm:grid-cols-3 gap-6" data-testid="impact-stats">
              {stats.map((s, i) => (
                <Reveal key={i} delay={i * 0.1} className="rounded-2xl bg-white border border-sylhet-forest/10 p-7" data-testid={`stat-${i}`}>
                  <div className="font-display text-4xl md:text-5xl font-extrabold text-sylhet-green tabular-nums">
                    <CountUp end={s.value} duration={2.2} separator="," enableScrollSpy scrollSpyOnce />
                    {s.value >= 1000 ? "+" : ""}
                  </div>
                  <p className="text-charcoal/55 mt-2 text-sm leading-snug">{s.label}</p>
                </Reveal>
              ))}
            </div>
            <p className="text-charcoal/35 text-xs mt-4 italic">Impact figures are placeholders pending client confirmation.</p>
          </div>
        </div>
      </section>

      {/* Quote band */}
      <section className="bg-sylhet-forest text-cream grain" data-testid="about-quote">
        <div className="max-w-content mx-auto px-6 md:px-12 py-24 md:py-32 relative z-10">
          <Reveal className="max-w-4xl">
            <span className="font-display text-7xl text-sylhet-gold/40 leading-none">"</span>
            <blockquote className="font-display text-3xl md:text-5xl font-medium leading-[1.15] -mt-6 text-balance">
              Charity does not decrease wealth. Made in Sylhet grows from that same spirit — a celebration of the
              region's people, land and culture, and a stage for its storytellers.
            </blockquote>
            <p className="text-cream/50 mt-8">— Guiding belief of the Fahim Al Choudhury Trust, Zakiganj</p>
          </Reveal>
        </div>
      </section>

      {/* nature strip */}
      <section className="grid grid-cols-2 md:grid-cols-4">
        {[IMAGES.haor, IMAGES.bisanakandi, IMAGES.ratargul, IMAGES.jaflong].map((src, i) => (
          <div key={i} className="aspect-square overflow-hidden">
            <img src={src} alt="Sylhet landscape" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" loading="lazy" />
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="max-w-content mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        <Reveal as="h2" className="font-display text-4xl md:text-6xl font-bold tracking-tight">Be part of the story</Reveal>
        <Reveal as="p" delay={0.08} className="text-charcoal/60 mt-4 max-w-xl mx-auto">Share your Sylhet, your way.</Reveal>
        <Link to="/submit" data-testid="about-cta" className="inline-block mt-8 px-9 py-4 rounded-full bg-sylhet-red text-cream font-semibold text-lg hover:-translate-y-1 hover:shadow-xl transition-all">
          Submit your Reel
        </Link>
      </section>

      <SiteFooter />
    </div>
  );
}
